// THE ONE STATUS VOCABULARY: six words for the six ProviderCardStatus values,
// plus the in-flight states a row holds while an RPC is out. Replaces BADGE_CLASS
// in both ProviderSteps and "Connected"/"Unavailable" in DaemonDetail.
import type { ProviderCardStatus, ProviderRow } from "$lib/provider-setup.js";
import type { ProviderReasonKind } from "$lib/plugins/agents/types.js";
import type { StatusTone } from "$lib/color/status-tone.js";

/** Client-side states: an optimistic status that OWNS the row until its RPC answers. */
export type ProviderFlowStatus =
  | "installing"
  | "install-failed"
  | "signing"
  | "adding"
  | "acp-ready"
  | "acp-missing"
  | "acp-failed"
  | "expired"
  | "usage-gated"
  | "key-invalid";

export type ProviderRowStatus = ProviderCardStatus | ProviderFlowStatus;

export interface StatusVocabularyEntry {
  word: string;
  tone: StatusTone;
}

/** Exported so a test asserts the vocabulary instead of a rendered pill. */
export const PROVIDER_STATUS_WORDS: Record<ProviderRowStatus, StatusVocabularyEntry> = {
  ready: { word: "Ready", tone: "ready" },
  "signed-out": { word: "Sign in needed", tone: "attention" },
  "not-installed": { word: "Not installed", tone: "neutral" },
  unverified: { word: "Couldn't check", tone: "neutral" },
  available: { word: "Available", tone: "offer" },
  "needs-key": { word: "Needs a key", tone: "neutral" },
  installing: { word: "Installing…", tone: "progress" },
  "install-failed": { word: "Install failed", tone: "failed" },
  signing: { word: "Waiting for browser…", tone: "progress" },
  adding: { word: "Setting up…", tone: "progress" },
  "acp-ready": { word: "Ready", tone: "ready" },
  "acp-missing": { word: "CLI not found", tone: "attention" },
  "acp-failed": { word: "Couldn't set up", tone: "failed" },
  expired: { word: "Sign in expired", tone: "attention" },
  "usage-gated": { word: "Plan out of usage", tone: "attention" },
  "key-invalid": { word: "Key rejected", tone: "failed" },
};

export const CANONICAL_STATUS_ORDER: readonly ProviderCardStatus[] = [
  "ready",
  "signed-out",
  "not-installed",
  "unverified",
  "available",
  "needs-key",
];

export function statusWord(status: ProviderRowStatus): string {
  return PROVIDER_STATUS_WORDS[status].word;
}

export function statusTone(status: ProviderRowStatus): StatusTone {
  return PROVIDER_STATUS_WORDS[status].tone;
}

const IN_FLIGHT = new Set<ProviderRowStatus>(["installing", "signing", "adding"]);

/** An RPC we started is still out: a re-probe must not overwrite it. */
export function isInFlight(status: ProviderRowStatus): boolean {
  return IN_FLIGHT.has(status);
}

export function isReadyStatus(status: ProviderRowStatus): boolean {
  return status === "ready" || status === "acp-ready";
}

/** Handed to an agent right now. A key row counts: the field is on the panel. */
export function isPickableStatus(status: ProviderRowStatus): boolean {
  return isReadyStatus(status) || status === "needs-key";
}

const NEEDS_SOMETHING = new Set<ProviderRowStatus>([
  "signed-out",
  "not-installed",
  "unverified",
  "acp-missing",
  "install-failed",
  "acp-failed",
  "expired",
  "usage-gated",
  "key-invalid",
]);

export function needsSomething(status: ProviderRowStatus): boolean {
  return NEEDS_SOMETHING.has(status);
}

// providerCardStatus() collapses these two (expired→signed-out,
// usage_gated→unverified) because a PICKER only needs the coarse answer.
const REFINEMENTS: Partial<Record<ProviderReasonKind, ProviderFlowStatus>> = {
  expired: "expired",
  usage_gated: "usage-gated",
};

/** Narrow a coarse status by reasonKind. What the daemon could NOT classify
 *  (rate_limited / unknown / internal_error) stays "Couldn't check". */
export function refineRowStatus(
  coarse: ProviderCardStatus,
  reasonKind: ProviderReasonKind | null | undefined,
): ProviderRowStatus {
  if (coarse !== "signed-out" && coarse !== "unverified") return coarse;
  const refined = reasonKind ? REFINEMENTS[reasonKind] : undefined;
  if (!refined) return coarse;
  if (refined === "expired" && coarse !== "signed-out") return coarse;
  if (refined === "usage-gated" && coarse !== "unverified") return coarse;
  return refined;
}

// The inverse of refineRowStatus: anything typed ProviderCardStatus (the picker
// vocabulary, providerSetupSteps, providerCardCopy) needs the coarse answer back.
const COARSE: Record<ProviderFlowStatus, ProviderCardStatus> = {
  installing: "not-installed",
  "install-failed": "not-installed",
  signing: "signed-out",
  adding: "available",
  "acp-ready": "ready",
  "acp-missing": "available",
  "acp-failed": "available",
  expired: "signed-out",
  "usage-gated": "unverified",
  "key-invalid": "needs-key",
};

export function coarseStatus(status: ProviderRowStatus): ProviderCardStatus {
  return (
    (COARSE as Partial<Record<ProviderRowStatus, ProviderCardStatus>>)[status] ??
    (status as ProviderCardStatus)
  );
}

/** The monogram a catalog id with no brand mark falls back to. */
export function providerMonogram(label: string, id: string): string {
  const source = (label || "").trim() || normalizeProviderId((id || "").trim());
  if (!source) return "?";
  const words = source.split(/[\s\-_./]+/).filter(Boolean);
  if (words.length > 1) {
    const first = words[0]?.[0] ?? "";
    const second = words[1]?.[0] ?? "";
    return (first + second).toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

/** ACP catalog ids carry an `-acp` suffix the popular list does not. */
export function normalizeProviderId(id: string): string {
  return id.replace(/-acp$/, "");
}

export function rowModelCount(row: ProviderRow | null): number {
  return row?.models?.length ?? 0;
}
